import {FmlxButton} from 'fmlx-common-ui';
import {useLocation} from 'react-router-dom';

/**
 * Custom hook to render footer buttons based on conditions.
 *
 * @param {Object} config - Configuration object for the footer buttons.
 * @param {string} config.pathCondition - A string to match against the current route pathname.
 * Example: '/Team' will prevent rendering buttons when the path includes '/Team'.
 *
 * @param {Array<Object>} config.buttons - An array of button configuration objects.
 * Each object defines properties for a button, including:
 *  - `label` {string} - The text displayed on the button.
 *  - `className` {string} - CSS class for styling the button.
 *  - `variant` {string} - Variant style (e.g., 'primary', 'outline').
 *  - `type` {string} - Button type (e.g., 'primary', 'danger', etc.).
 *  - `onClick` {function} - A callback function that runs when the button is clicked.
 *
 * Example of buttons param:
 * ```
 * [
 *   {
 *     label: 'REJECT',
 *     className: 'reject',
 *     variant: 'outline',
 *     type: 'danger',
 *     onClick: () => handleRejectClick(selectedMessage),
 *   },
 *   {
 *     label: 'PUBLISH',
 *     className: 'publish',
 *     type: 'primary',
 *     onClick: () => handlePublishClick(selectedMessage),
 *   }
 * ]
 *```
 * @param {function} [config.conditionCheck] - Optional custom function to determine when to render buttons.
 * This function receives the current pathname as an argument and returns a boolean.
 * Use this for more complex conditional logic if the `pathCondition` string alone isn't sufficient.
 *
 * Example of conditionCheck param:
 * ```
 * const customConditionCheck = (path) => path === '/specific-path';
 *```

 * @returns {Object} - Returns an object containing:
 *  - `renderFooterButtons` {function} - Function that returns JSX for rendering the footer buttons based on conditions.
 */
export const useMessageFooter = ({pathCondition, buttons, conditionCheck}) => {
  const location = useLocation();
  const pathName = location.pathname;

  const renderFooterButtons = () => {
    const shouldRenderButtons = conditionCheck ? conditionCheck(pathName) : !pathName.includes(pathCondition);

    if (shouldRenderButtons) {
      return (
        <div className="modal-footer">
          {buttons.map(({label, className, variant, type, onClick}, index) => (
            <FmlxButton
              key={index}
              className={className}
              label={label}
              variant={variant}
              type={type}
              onClick={onClick}
            />
          ))}
        </div>
      );
    } else {
      return false;
    }
  };

  return {renderFooterButtons};
};
